import React from 'react';

import PropTypes from 'prop-types';

import Text from '@zenbusiness/zui/Text';

import { TRANSITION_TYPES } from '#client/components/utils/constants';
import VeloAvatar from '#client/components/VeloAvatar';

import * as Styled from './Transition.style';
import NoSSR from '../utils/noSSR';

/**
 * A reusable transition component that displays content with customizable title, subtitle, and actions.
 * @param {object} props
 * @param {keyof TRANSITION_TYPES} props.type - Predefined transition type (optional)
 * @param {string} props.title - Custom title (overrides type-based title)
 * @param {string} props.subtitle - Custom subtitle (overrides type-based subtitle)
 * @param {React.ReactNode} props.actions - Action buttons/elements
 * @param {React.ReactNode} props.icon - Custom icon (overrides type-based logo)
 * @param {object} props.typeProps - Props to pass to type-based subtitle functions (e.g., {retryCount: 2})
 * @param {object} props.restProps - Rest props to pass to the container
 * @returns {React.ReactElement} A div element with the transition content
 */
const Transition = ({ type, title, subtitle, actions, icon, typeProps = {}, ...restProps }) => {
  // Filter out LoadingZone context props to prevent React warnings about unrecognized DOM attributes
  const { childStatus, childCount, status, total, progress, finished, stage, ...cleanProps } =
    restProps;

  // Get configuration from type if provided
  const typeConfig = type ? TRANSITION_TYPES[type] : null;

  // Determine final values (custom props override type-based ones)
  const finalTitle = title || typeConfig?.title || '';
  const finalSubtitle =
    subtitle ||
    (typeof typeConfig?.subtitle === 'function'
      ? typeConfig.subtitle(typeProps)
      : typeConfig?.subtitle) ||
    '';

  // Determine icon - custom icon > type-based logo > default
  let finalIcon = icon;
  if (!finalIcon && typeConfig?.logoState) {
    finalIcon = <VeloAvatar height={148} state={typeConfig.logoState} width={148} />;
  }
  if (!finalIcon) {
    finalIcon = <VeloAvatar height={148} state="default" width={148} />;
  }

  return (
    <Styled.Container {...cleanProps}>
      <NoSSR>{finalIcon}</NoSSR>
      {finalTitle && (
        <Text fontSize="md" fontWeight="600" marginTop={2}>
          {finalTitle}
        </Text>
      )}
      {finalSubtitle && (
        <Text fontSize="xs" marginTop={0}>
          {finalSubtitle}
        </Text>
      )}
      {actions && <Styled.Actions>{actions}</Styled.Actions>}
    </Styled.Container>
  );
};

Transition.propTypes = {
  type: PropTypes.oneOf(Object.keys(TRANSITION_TYPES)),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  actions: PropTypes.node,
  icon: PropTypes.node,
  typeProps: PropTypes.object
};

export default Transition;
